import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { getGuideBySlug, trilhaGuias, TRILHA_NAME } from '../data/guias';
import { glossarioTerms } from '../data/glossario';
import { NotFoundPage } from './NotFoundPage';

const SITE_NAME = import.meta.env.VITE_SITE_NAME || 'Cripto News';
const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://criptonews.site';

export const GuiaPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const guide = slug ? getGuideBySlug(slug) : undefined;

  if (!guide) return <NotFoundPage />;

  const canonicalUrl = `${SITE_URL}/aprenda/${guide.slug}`;
  const relatedTerms = guide.glossario
    ? glossarioTerms.filter((t) => guide.glossario!.includes(t.slug))
    : [];

  const inTrilha = guide.trilhaOrder !== undefined;
  const prevGuide = inTrilha ? trilhaGuias.find((g) => g.trilhaOrder === guide.trilhaOrder! - 1) : undefined;
  const nextGuide = inTrilha ? trilhaGuias.find((g) => g.trilhaOrder === guide.trilhaOrder! + 1) : undefined;

  const jsonLd: any[] = [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: guide.title,
      description: guide.description,
      url: canonicalUrl,
      inLanguage: 'pt-BR',
      author: { '@type': 'Organization', name: SITE_NAME, url: SITE_URL },
      publisher: { '@type': 'Organization', name: SITE_NAME, url: SITE_URL },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Início', item: SITE_URL },
        { '@type': 'ListItem', position: 2, name: 'Aprenda', item: `${SITE_URL}/aprenda` },
        { '@type': 'ListItem', position: 3, name: guide.title, item: canonicalUrl },
      ],
    },
  ];
  if (guide.faq && guide.faq.length > 0) {
    jsonLd.push({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: guide.faq.map((f) => ({
        '@type': 'Question',
        name: f.question,
        acceptedAnswer: { '@type': 'Answer', text: f.answer },
      })),
    });
  }

  return (
    <>
      <Helmet>
        <title>{guide.title} | Aprenda | {SITE_NAME}</title>
        <meta name="description" content={guide.description} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content={guide.title} />
        <meta property="og:description" content={guide.description} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-8 max-w-3xl">
          <nav className="text-sm text-gray-500 mb-4 flex items-center gap-2">
            <Link to="/" className="hover:text-blue-600 transition">Início</Link>
            <span>›</span>
            <Link to="/aprenda" className="hover:text-blue-600 transition">Aprenda</Link>
            <span>›</span>
            <span className="text-gray-700 truncate max-w-xs">{guide.title}</span>
          </nav>

          {inTrilha && (
            <p className="text-xs font-bold text-blue-600 uppercase tracking-wider mb-2">
              🎓 {TRILHA_NAME} — lição {guide.trilhaOrder} de {trilhaGuias.length}
            </p>
          )}

          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">{guide.title}</h1>
          <p className="text-gray-500 text-sm mb-6">
            {guide.category} · {guide.minutes} min de leitura · conteúdo educativo
          </p>

          <div className="bg-white rounded-xl shadow p-6 md:p-8 mb-6">
            {guide.sections.map((section, index) => (
              <section key={index} className={index > 0 ? 'mt-7' : ''}>
                <h2 className="text-xl font-bold text-gray-900 mb-3">{section.heading}</h2>
                {section.paragraphs?.map((paragraph, pIndex) => (
                  <p key={pIndex} className="text-gray-700 leading-relaxed text-[15px] mb-3">
                    {paragraph}
                  </p>
                ))}
                {section.list && (
                  <ul className="space-y-2 mt-1">
                    {section.list.map((item, lIndex) => (
                      <li key={lIndex} className="flex items-start gap-2 text-gray-700 text-[15px] leading-relaxed">
                        <span className="flex-shrink-0 mt-2 w-1.5 h-1.5 rounded-full bg-blue-500" aria-hidden />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            ))}
          </div>

          {/* FAQ */}
          {guide.faq && guide.faq.length > 0 && (
            <section className="bg-white rounded-xl shadow p-6 md:p-8 mb-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Perguntas frequentes</h2>
              <div className="space-y-5">
                {guide.faq.map((faq, index) => (
                  <div key={index}>
                    <h3 className="font-semibold text-gray-900 mb-1">{faq.question}</h3>
                    <p className="text-gray-600 text-[15px] leading-relaxed">{faq.answer}</p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Termos do glossário */}
          {relatedTerms.length > 0 && (
            <section className="bg-white rounded-xl shadow p-6 mb-6">
              <h2 className="text-lg font-bold text-gray-900 mb-3">Termos citados neste guia</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {relatedTerms.map((term) => (
                  <Link
                    key={term.slug}
                    to={`/glossario/${term.slug}`}
                    className="border border-gray-100 rounded-lg p-3 hover:border-blue-200 hover:bg-blue-50 transition"
                  >
                    <p className="font-semibold text-gray-900 text-sm mb-0.5">{term.termo}</p>
                    <p className="text-gray-600 text-xs leading-relaxed line-clamp-2">{term.resumo}</p>
                  </Link>
                ))}
              </div>
            </section>
          )}

          {/* Navegação da trilha */}
          {inTrilha && (
            <div className="flex flex-col sm:flex-row gap-3 mb-6">
              {prevGuide ? (
                <Link
                  to={`/aprenda/${prevGuide.slug}`}
                  className="flex-1 bg-white rounded-xl shadow p-4 hover:shadow-lg transition"
                >
                  <p className="text-xs text-gray-400 mb-0.5">← Lição anterior</p>
                  <p className="font-semibold text-gray-900 text-sm">{prevGuide.title}</p>
                </Link>
              ) : (
                <div className="flex-1" />
              )}
              {nextGuide ? (
                <Link
                  to={`/aprenda/${nextGuide.slug}`}
                  className="flex-1 bg-blue-600 rounded-xl shadow p-4 hover:bg-blue-700 transition text-white text-right"
                >
                  <p className="text-xs text-blue-200 mb-0.5">Próxima lição →</p>
                  <p className="font-semibold text-sm">{nextGuide.title}</p>
                </Link>
              ) : (
                <Link
                  to="/aprenda"
                  className="flex-1 bg-green-600 rounded-xl shadow p-4 hover:bg-green-700 transition text-white text-right"
                >
                  <p className="text-xs text-green-200 mb-0.5">🎉 Trilha concluída!</p>
                  <p className="font-semibold text-sm">Explorar mais guias</p>
                </Link>
              )}
            </div>
          )}

          <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-xs text-amber-800 leading-relaxed">
            <strong>⚠️ Aviso:</strong> este é um conteúdo exclusivamente educativo e não constitui
            recomendação de investimento. Criptoativos envolvem alto risco e volatilidade; nunca invista mais
            do que pode perder.
          </div>
        </div>
      </div>
    </>
  );
};
